// src/hooks/web/useOrder.js
import { useState, useCallback } from "react";
import { useCartContext } from "@/contexts/CartContext";
import axios from "axios";
import { API } from "@/utils/web/constants";

export function useOrder() {
    const { cartItems, getTotalPrice, clearCart } = useCartContext();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [orderNumber, setOrderNumber] = useState(null);

    // Xử lý đặt hàng
    const placeOrder = useCallback(
        async (customerInfo) => {
            try {
                // Validate thông tin khách hàng trước khi đặt hàng
                if (
                    !customerInfo.fullName ||
                    !customerInfo.phone ||
                    !customerInfo.address
                ) {
                    throw new Error("Vui lòng điền đầy đủ thông tin");
                }

                // Validate giỏ hàng
                if (cartItems.length === 0) {
                    throw new Error("Giỏ hàng trống");
                }

                setLoading(true);
                setError(null);

                // Format cart items to match API expectation
                const formattedItems = cartItems.map(item => ({
                    product_variant_id: item.variantId,
                    quantity: item.quantity,
                    unit_price: item.price
                }));

                // Format order data according to API requirements
                const orderPayload = {
                    order: {
                        customer_name: customerInfo.fullName,
                        customer_phone: customerInfo.phone,
                        payment_method: "COD", // Default to COD for now
                        delivery_address: customerInfo.address,
                        notes: customerInfo.notes || ""
                    },
                    items: formattedItems
                };

                // Make the API call to create the order
                const response = await axios.post(
                    `${API.BASE_URL}${API.ORDER.CREATE}`,
                    orderPayload,
                    {
                        headers: {
                            'Content-Type': 'application/json',
                        }
                    }
                );

                // Process the API response
                if (response.data.isSuccess) {
                    const orderId = response.data.data.order.id;
                    setOrderNumber(orderId);
                    // Don't set order success yet - we'll do that with a delay
                    // Clear cart immediately though
                    clearCart();
                    return {
                        success: true,
                        orderNumber: orderId
                    };
                } else {
                    throw new Error(
                        response.data.message || "Đặt hàng thất bại"
                    );
                }
            } catch (err) {
                console.error("Order error:", err); // Thêm log để debug
                // Handle different types of errors
                const errorMessage = err.response?.data?.message || 
                                    err.message || 
                                    "Đặt hàng thất bại. Vui lòng thử lại sau.";
                setError(errorMessage);
                setOrderSuccess(false);
                return {
                    success: false,
                    error: errorMessage,
                };
            } finally {
                setLoading(false);
            }
        },
        [cartItems, getTotalPrice, clearCart]
    );

    // Set order success with or without delay
    const setOrderSuccessState = useCallback((success, delay = 0) => {
        if (delay > 0) {
            setTimeout(() => {
                setOrderSuccess(success);
            }, delay);
        } else {
            setOrderSuccess(success);
        }
    }, []);

    // Reset trạng thái đơn hàng
    const resetOrderState = useCallback(() => {
        setOrderSuccess(false);
        setOrderNumber(null);
        setError(null);
    }, []);

    return {
        placeOrder,
        loading,
        error,
        orderSuccess,
        orderNumber,
        resetOrderState,
        setOrderSuccessState
    };
}
